/* eslint-disable no-case-declarations */
import { combineReducers } from "redux"
import { createResponsiveStateReducer } from "redux-responsive"

import { jewlr_t } from "helpers/application"
import { creditCardCheckoutMethod } from "helpers/checkout/checkout_options"
import { joinNames } from "helpers/name"

import yup from "./yup"

const filterValuesByErrors = (values, errors) => {
  Object.keys(errors).forEach((key) => {
    if (errors[key] instanceof Object) {
      values[key] = filterValuesByErrors(
        Object.assign({}, values[key]),
        errors[key]
      )
    } else {
      delete values[key]
    }
  })
  return values
}

function order(
  state = {
    amazon_checkout_url: null,
    amazon_order_reference_id: null,
    cc_error: null,
    delivery: null,
    error: null,
    error_code: null,
    error_modal: false,
    loading: true,
    selectedPaymentMethod: null,
    show_facebook_messenger: false,
  },
  action
) {
  let newState = Object.assign({}, state)
  switch (action.type) {
    case "SAVE_KLARNA_CLIENT_TOKEN":
      newState.klarna_widget_info = action.klarnaWidgetInfo
      return newState
    case "AMAZON_SET_ORDER_REFERENCE":
      newState.amazon_order_reference_id = action.referenceId
      return newState
    case "ERROR_MODAL_OPEN":
      newState.error_modal = true
      newState.error = action.error_msg || newState.error
      return newState
    case "ERROR_MODAL_CLOSE":
      newState.error_modal = false
      return newState
    case "LOAD_ORDER_DATA":
    case "PRELOAD_ORDER_DATA":
      newState = {
        ...action.data,
        loading: false,
        selectedPaymentMethod: newState.selectedPaymentMethod,
      }
      return newState
    case "REMOVE_COUPON":
      newState.coupon_error = false
      return newState
    case "SET_DELIVERY_METHODS":
      newState.delivery = action.data
      const newShipMethod = action.data.available_ship_methods.filter(
        (sm) => sm.service_type == action.data.current_ship_method
      )
      if (newShipMethod.length > 0) {
        newState.order.shipping_adjustment.description =
          newShipMethod[0].service_label
        newState.order.shipping_adjustment.carrier_label = newShipMethod[0]
          .carrier_label
          ? ` (${newShipMethod[0].carrier_label})`
          : ""
      }
      return newState
    case "SET_ORDER_SUBMIT_RESULTS":
      newState.cc_error = action.results.cc_error
      newState.error_code = action.results.error_code
      newState.error = action.results.error
      newState.success = action.results.success
      if (action.results.react) {
        newState.order = action.results.react
      }

      return newState
    case "SET_PAYMENT_METHOD":
      newState.payment_method = action.paymentMethod
      newState.selectedPaymentMethod = action.paymentMethod
      if (action.paymentMethod !== "amazon_pay") {
        newState.is_new_amazon = false
      }
      return newState
    case "SET_USER_ON_CHECKOUT_LOGIN":
      newState.order.user = action.user
      if (action.data.order) {
        newState.order = action.data.order
      }
      return newState
    case "UPDATE_ORDER_DATA":
      newState.order = action.data.react
      newState.coupon_error = action.data.coupon_message || state.coupon_error
      newState.error = action.data.error
      newState.gift_card_error = action.data.gift_card_message
      newState.show_facebook_messenger =
        action.data.show_facebook_messenger || false
      newState.reward_error = action.data.reward_error
      return newState
    case "UPDATE_ORDER_VALUES":
    case "UPDATE_ORDER_VALUES_NOW":
      const values = action.props.values
      // Find the shipping method ID, and pre-emptively update the
      // order summary with its price before order update AJAX load
      // is completed
      const shipMethodId = values.ship_method_id
      const shipMethod =
        state.delivery && state.delivery.available_ship_methods
          ? state.delivery.available_ship_methods.find(
              (sm) => sm.service_type == shipMethodId
            )
          : null
      if (shipMethod && newState.order.shipping_adjustment) {
        // Get the shipping price delta and update the order total until the AJAX loads
        const shipPriceDelta =
          shipMethod.shipping_charge - parseFloat(state.order.totals.shipping)
        newState.order.shipping_adjustment.description =
          shipMethod.service_label
        newState.order.shipping_adjustment.carrier_label = shipMethod.carrier_label
          ? ` (${shipMethod.carrier_label})`
          : ""
        newState.order.totals.shipping = shipMethod.shipping_charge
        newState.order.totals.order_total =
          parseFloat(state.order.totals.order_total) + shipPriceDelta
      }
      return newState
    default:
      return state
  }
}

function panels(state = [], action) {
  let newState = state.slice()
  switch (action.type) {
    case "LOAD_ORDER_DATA":
    case "PRELOAD_ORDER_DATA":
      let panels = [
        {
          actionTitle: "Contact Information",
          attempted: false,
          complete: false,
          fields: [
            "order.email",
            "order.account_create_password",
            "account_create_error",
          ],
          hasCompleted: false,
          name: "contact",
          opened: false,
          title: "Contact Information",
          tooltip:
            "The email address will be used for order and shipping notification. The phone number will be used to contact you if required.",
        },
      ]

      panels = [
        ...panels,
        {
          actionTitle: `${jewlr_t("terms.Shipping")} Address`,
          attempted: false,
          complete: false,
          fields: [
            "ship_full_name",
            "order.ship_address_attributes.address1",
            "order.ship_address_attributes.address2",
            "order.ship_address_attributes.city",
            "order.ship_address_attributes.state",
            "order.ship_address_attributes.postal_code",
            "order.ship_address_attributes.country",
          ],
          hasCompleted: false,
          name: "ship_address",
          opened: false,
          title: `${jewlr_t("terms.Shipping")} Address`,
        },
        {
          actionTitle: `${jewlr_t("terms.Shipping")} Options`,
          attempted: false,
          complete: false,
          fields:
            order.site_country === "CA"
              ? ["order.ship_address_attributes.postal_code"]
              : ["ship_method_id"],
          hasCompleted: false,
          name: "ship_methods",
          opened: false,
          title: `${jewlr_t("terms.Shipping")} Options`,
        },
        {
          actionTitle: "Payment",
          attempted: false,
          complete: false,
          fields: [
            "order.customer_phone",
            "cc.cc_number",
            "cc.expiry",
            "cc.cc_expiry_month",
            "cc.cc_expiry_year",
            "cc.cc_verification_value",
            "bill_full_name",
            "order.bill_address_attributes.phone",
            "order.bill_address_attributes.address1",
            "order.bill_address_attributes.address2",
            "order.bill_address_attributes.city",
            "order.bill_address_attributes.state",
            "order.bill_address_attributes.postal_code",
            "order.bill_address_attributes.country",
          ],
          hasCompleted: false,
          name: "payment_methods",
          opened: false,
          title: "Payment",
        },
      ]

      newState = panels

      return newState
    case "PANEL_ATTEMPTED":
      newState[action.step] = Object.assign({}, newState[action.step], {
        attempted: true,
      })
      return newState
    case "PANEL_COMPLETE":
      let shouldOpen = false
      if (action.actionType === "toggle") {
        shouldOpen = !state[action.step].opened
      } else if (action.actionType === "open") {
        shouldOpen = true
      }

      newState[action.step] = Object.assign({}, newState[action.step], {
        complete: true,
        hasCompleted: true,
        opened: shouldOpen,
      })
      return newState
    case "PANEL_INCOMPLETE":
      newState[action.step] = Object.assign({}, newState[action.step], {
        complete: false,
        opened: action.actionType === "open",
      })
      return newState
    case "PANEL_OPEN":
      newState[action.step] = Object.assign({}, newState[action.step], {
        opened: true,
      })
      return newState
    case "PANEL_TOGGLE":
      newState[action.step] = Object.assign({}, newState[action.step], {
        opened: !state[action.step].opened,
      })
      return newState
    default:
      return state
  }
}

function form(
  state = {
    addGiftCard: false,
    applyCoupon: false,
    applyGiftCard: false,
    applyRewards: false,
    ccCheckoutMethod: "stripeCheckout",
    checkGiftCard: false,
    countrySwitch: false,
    errors: {
      order: {},
    },
    hydrated: false,
    initialValues: {},
    isReady: true,
    lastStep: 4,
    loadingShippingMethods: false,
    paymentMethod: null,
    removeCoupon: false,
    removeGiftCard: false,
    removeGiftCardId: null,
    schema: null,
    values: {},
  },
  action
) {
  let newState = Object.assign({}, state)
  switch (action.type) {
    case "ADD_GIFT_CARD":
      newState.addGiftCard = true
      return newState
    case "APPLY_GIFT_CARD":
      newState.applyGiftCard = true
      return newState
    case "CHECK_GIFT_CARD":
      newState.checkGiftCard = true
      return newState
    case "LOAD_ORDER_DATA":
    case "PRELOAD_ORDER_DATA":
      const data = action.data
      newState.initialValues = {
        bill_full_name: joinNames(
          data.order.bill_address.first_name || data.order.customer.first_name,
          data.order.bill_address.last_name || data.order.customer.last_name
        ),
        cc: {
          cc_expiry_month: data.cc.expiry_month || "",
          cc_expiry_year: data.cc.expiry_year || "",
          cc_number: data.cc.number || "",
          cc_verification_value: data.cc.verification_value || "",
          expiry: "",
        },
        coupon_code: data.coupon_code || "",
        customer_full_name: joinNames(
          data.order.bill_address.first_name || data.order.customer.first_name,
          data.order.bill_address.last_name || data.order.customer.last_name
        ),
        gift_code: "",
        gift_ids: data.order.line_items.map((li) => li.id),
        gift_message: data.order.line_items.map((li) => li.gift_message),
        gift_package: data.order.line_items.map((li) => li.selected_packaging),
        order: {
          account_create_error: "",
          account_create_password: "",
          balance: data.order.balance,
          bill_address_attributes: {
            address1: data.order.bill_address.address1 || "",
            address2: data.order.bill_address.address2 || "",
            city: data.order.bill_address.city || "",
            country: data.order.bill_address.country || "",
            first_name:
              data.order.bill_address.first_name ||
              data.order.customer.first_name ||
              "",
            id: data.order.bill_address.id,
            last_name:
              data.order.bill_address.last_name ||
              data.order.customer.last_name ||
              "",
            phone:
              data.order.bill_address.phone || data.order.customer.phone || "",
            postal_code: data.order.bill_address.postal_code || "",
            state: data.order.bill_address.state || "",
          },
          create_account: data.order.customer.create_account || false,
          customer_first_name:
            data.order.bill_address.first_name ||
            data.order.customer.first_name,
          customer_last_name:
            data.order.bill_address.last_name || data.order.customer.last_name,
          customer_phone: data.order.customer.phone || "",
          email: data.order.user
            ? data.order.user.email
            : data.order.customer.email || "",
          opt_in:
            ["undefined", "null"].indexOf(typeof data.order.customer.opt_in) !==
            -1
              ? data.order.customer.opt_in
              : data.order.customer.default_opt_in,
          payment_method: data.payment_method,
          same_as_shipping_address_b: data.order.billing_same_as_shipping,
          ship_address_attributes: {
            address1: data.order.ship_address.address1 || "",
            address2: data.order.ship_address.address2 || "",
            city: data.order.ship_address.city || "",
            country: data.order.ship_address.country || "",
            first_name:
              data.order.ship_address.first_name ||
              data.order.customer.first_name ||
              "",
            id: data.order.ship_address.id,
            label: data.order.ship_address.label || "",
            last_name:
              data.order.ship_address.last_name ||
              data.order.customer.last_name ||
              "",
            phone:
              data.order.ship_address.phone || data.order.customer.phone || "",
            postal_code: data.order.ship_address.postal_code || "",
            state: data.order.ship_address.state || "",
          },
          sms_opt_in: data.order.customer.sms_opt_in || false,
        },
        order_balance: data.order.balance,
        order_token: data.order.token,
        payment_method: data.payment_method,
        paypal_unsuccessful_cart_checkout: data.paypal_unsuccessful
          ? "true"
          : null,
        recipient_name: data.order.line_items.map((li) => li.recipient_name),
        reward_amount: data.order.points.redeem_value || "",
        same_as_shipping_address_b: data.order.billing_same_as_shipping,
        ship_full_name: joinNames(
          data.order.ship_address.first_name || data.order.customer.first_name,
          data.order.ship_address.last_name || data.order.customer.last_name
        ),
        ship_method_id: data.delivery.current_ship_method,
        zip_mismatch_error: false,
      }
      newState.values = newState.initialValues
      newState.ccCheckoutMethod = creditCardCheckoutMethod()
      newState.schema = yup(data.order, newState.ccCheckoutMethod)
      newState.paymentMethod = data.payment_method
      return newState
    case "REDEEM_POINTS":
      newState.initialValues.reward_amount = action.amount
      newState.values.reward_amount = action.amount
      return newState
    case "REDEEM_REWARDS":
      newState.applyRewards = true
      return newState
    case "REMOVE_REWARDS":
      newState.initialValues.reward_amount = ""
      newState.values.reward_amount = ""
      return newState
    case "REMOVE_GIFT_CARD":
      newState.removeGiftCard = true
      newState.removeGiftCardId = action.adjId
      return newState
    case "SET_LOADING_SHIPPING_METHODS":
      newState.loadingShippingMethods = action.loading
      return newState
    case "SET_DELIVERY_METHODS":
      newState.initialValues.ship_method_id = action.data.current_ship_method
      newState.loadingShippingMethods = false
      return newState
    case "SET_PAYMENT_METHOD":
      newState.paymentMethod = action.paymentMethod
      return newState
    case "SHOW_COUNTRY_SWITCH":
      newState.countrySwitch = !state.countrySwitch
      return newState
    case "UPDATE_ORDER_DATA":
      newState.schema = yup(action.data.react, newState.ccCheckoutMethod)
      if (action.data.gift_card_success) {
        newState.addGiftCard = false
      }
      newState.applyCoupon = false
      newState.applyGiftCard = false
      newState.checkGiftCard = false
      newState.removeCoupon = false
      newState.removeGiftCard = false
      newState.removeGiftCardId = null
      newState.applyRewards = false
      return newState
    case "UPDATE_ORDER_VALUES":
    case "UPDATE_ORDER_VALUES_NOW":
      // We reject the values that don't have correct data
      let newValues = Object.assign({}, action.props.values)
      newValues = filterValuesByErrors(newValues, action.props.errors)
      newState.errors = action.props.errors
      newState.values = newValues
      newState.hydrated = true
      return newState
    case "SET_LOGIN_CHECKOUT":
      newState.checkoutLogin = true
      return newState
    case "SET_USER_ON_CHECKOUT_LOGIN":
      let oldValues = Object.assign({}, newState.values)
      let updatedValues = {
        ...oldValues,
        order: { email: action.email },
      }

      newState.values = updatedValues
      if (!action.email) {
        newState.errors = {
          ...newState.errors,
          order: {
            ...newState.errors.order,
            email: "Please enter a valid email address",
          },
        }
      }
      newState.hydrated = true
      if (action.data.order) {
        newState.schema = yup(action.data.order, newState.ccCheckoutMethod)
      }
      return newState
    case "SET_CHECKOUT_READY":
      newState.isReady = action.isReady
      return newState
    default:
      return state
  }
}

function experiment(state = {}, action) {
  let newState = Object.assign({}, state)
  switch (action.type) {
    case "SET_EXPERIMENT":
      newState = { ...state, ...action.experiment }
      return newState
    default:
      return state
  }
}

const checkoutApp = combineReducers({
  browser: createResponsiveStateReducer({
    desktop: 1024,
    mobile: 767,
    widescreen: 9999,
  }),
  experiment,
  form,
  order,
  panels,
})
export default checkoutApp
